import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { WarningIcon } from './icons/WarningIcon'

interface ConfirmDialogProps {
  title: string
  message?: string
  body?: ReactNode
  warningBody?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
  requiresAcknowledgment?: boolean
  acknowledgmentLabel?: string
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  title,
  message,
  body,
  warningBody,
  confirmLabel,
  cancelLabel,
  danger = false,
  requiresAcknowledgment = false,
  acknowledgmentLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation()
  const [acknowledged, setAcknowledged] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<Element | null>(null)

  useFocusTrap(dialogRef)

  useEffect(() => {
    triggerRef.current = document.activeElement
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus()
      }
    }
  }, [onCancel])

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        className="modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="confirm-dialog-title">{title}</h3>
        </div>
        {(message || body || warningBody) && (
          <div className="modal-body">
            {message && <p>{message}</p>}
            {body}
            {warningBody && (
              <div className="warn-card">
                <WarningIcon size={16} />
                <div>{warningBody}</div>
              </div>
            )}
          </div>
        )}
        {requiresAcknowledgment && (
          <div className="modal-acknowledgment">
            <label className="acknowledgment-label">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              {acknowledgmentLabel ?? t('common.iUnderstand')}
            </label>
          </div>
        )}
        <div className="modal-footer">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-ghost"
          >
            {cancelLabel ?? t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={requiresAcknowledgment && !acknowledged}
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
          >
            {confirmLabel ?? t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}
